import { makeStyles, tokens } from '@fluentui/react-components';
import { TIER_COLORS } from '../constants/tiers';

export const useProgramChangesPageStyles = makeStyles({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '12px',
  },
  lastUpdated: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: tokens.colorNeutralForeground3,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '8px',
  },
  tabContent: {
    marginTop: '20px',
  },
  card: {
    padding: '24px',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  tableHeaderCell: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  tableHeaderCellActions: {
    padding: '12px 16px',
    textAlign: 'right',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
  },
  tableRow: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tableCell: {
    padding: '16px',
    verticalAlign: 'middle',
  },
  tableCellActions: {
    padding: '16px',
    textAlign: 'right',
    verticalAlign: 'middle',
  },
  categoryName: {
    fontWeight: 600,
    minWidth: '160px',
  },
  categoryNameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    minWidth: '160px',
  },
  /**
   * Wraps the category/benefit name and the "Last updated …" subtitle in a
   * vertical stack so the cell stays compact while still surfacing the
   * applied-history metadata. Matches the styling of pending badges that
   * already share the cell.
   */
  nameWithLastUpdatedStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    minWidth: 0,
  },
  lastUpdatedSubtitle: {
    fontSize: '11px',
    lineHeight: '14px',
    color: tokens.colorNeutralForeground3,
    fontWeight: 400,
  },
  categoryNamePendingCreate: {
    color: tokens.colorPaletteGreenForeground1,
  },
  categoryNamePendingRename: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontStyle: 'italic',
  },
  categoryNamePendingDelete: {
    color: tokens.colorPaletteRedForeground1,
    textDecoration: 'line-through',
  },
  categoryNameSwatchCreate: {
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: 600,
  },
  categoryNameSwatchRename: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontWeight: 600,
    fontStyle: 'italic',
  },
  categoryNameSwatchDelete: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 600,
    textDecoration: 'line-through',
  },
  countriesList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  countryBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '28px',
    padding: '0 12px',
    borderRadius: '9999px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  countryBadgeAdded: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
    border: `1px solid ${tokens.colorPaletteGreenBorderActive}`,
  },
  countryBadgeRemoved: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    border: `1px solid ${tokens.colorPaletteRedBorderActive}`,
    textDecoration: 'line-through',
  },
  pendingLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    marginBottom: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  pendingLegendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  pendingLegendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '9999px',
    border: '1px solid transparent',
  },
  placeholderContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    color: tokens.colorNeutralForeground3,
  },
  danger: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
     ':hover': {
       backgroundColor: tokens.colorPaletteRedForeground1,
     },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '8px',
  },
  toolbarSpacer: {
    flex: 1,
  },
  benefitsCount: {
    color: tokens.colorNeutralForeground3,
    padding: '8px 4px',
  },
  tierHeaderCell: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  tierHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tierDiamondSilver: { color: TIER_COLORS.Silver },
  tierDiamondGold: { color: TIER_COLORS.Gold },
  tierDiamondPlatinum: { color: TIER_COLORS.Platinum },
  benefitRow: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  benefitRowEditing: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  benefitNameCell: {
    padding: '12px 16px',
    verticalAlign: 'middle',
    minWidth: '260px',
  },
  benefitTierCell: {
    padding: '12px 16px',
    verticalAlign: 'middle',
    color: tokens.colorNeutralForeground2,
  },
  benefitActionsCell: {
    padding: '8px 16px',
    verticalAlign: 'middle',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  editActions: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    width: '100%',
  },
  editActionsSpacer: {
    flex: 1,
    minWidth: '16px',
  },
  selectionCell: {
    padding: '12px 8px 12px 16px',
    width: '32px',
    verticalAlign: 'middle',
  },
  benefitNameContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
  deleteButton: {
    color: tokens.colorPaletteRedForeground1,
    ':hover': {
      color: tokens.colorPaletteRedForeground1,
      backgroundColor: tokens.colorPaletteRedBackground2,
    },
  },
  benefitNameInput: {
    flex: '0 0 auto',
    width: '150px',
    minWidth: '150px',
    maxWidth: '100%',
  },
  inlineError: {
    marginTop: '6px',
    color: 'var(--colorPaletteRedForeground1)',
    maxWidth: '100%',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  inlineErrorText: {
    color: 'var(--colorPaletteRedForeground1)',
    whiteSpace: 'nowrap',
    marginLeft: '4px',
  },
  pendingInfoIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--colorPaletteBlueForeground2)',
    cursor: 'help',
    fontSize: '16px',
    lineHeight: 1,
    flexShrink: 0,
    '& svg': {
      width: '16px',
      height: '16px',
    },
    ':focus-visible': {
      outline: '2px solid var(--colorStrokeFocus2)',
      outlineOffset: '2px',
      borderRadius: '50%',
    },
  },
  pendingTierChange: {
    fontWeight: 600,
    color: 'var(--colorPaletteBlueForeground2)',
    cursor: 'help',
  },
  pendingTierChangeFrom: {
    fontWeight: 400,
    fontStyle: 'italic',
    marginLeft: '4px',
    color: 'var(--colorNeutralForeground3)',
  },
  tierEligibilityAdded: {
    fontWeight: 600,
    color: tokens.colorPaletteGreenForeground1,
  },
  tierEligibilityRemoved: {
    fontWeight: 600,
    color: tokens.colorPaletteRedForeground1,
    textDecoration: 'line-through',
  },
  tierEligibilityAddedSwatch: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    border: `1px solid ${tokens.colorPaletteGreenBorderActive}`,
  },
  tierEligibilityRemovedSwatch: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    border: `1px solid ${tokens.colorPaletteRedBorderActive}`,
  },
  gridContainer: {
    width: '100%',
    overflow: 'hidden',
  },
  thresholdInput: {
    width: '100%',
    minWidth: '100px',
  },
  deleteDialogWarning: {
    marginTop: '12px',
  },
  thresholdCell: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
    lineHeight: 1.2,
  },
  thresholdValueChanged: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: tokens.colorPaletteGreenBackground1,
    color: tokens.colorPaletteGreenForeground1,
    border: `1px solid ${tokens.colorPaletteGreenBorderActive}`,
    fontWeight: 600,
  },
  thresholdValuePrevious: {
    color: tokens.colorPaletteRedForeground1,
    textDecoration: 'line-through',
    fontSize: '12px',
  },
  countriesEditor: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  countryBadgeContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  countryRemoveButton: {
    minWidth: 'auto',
    padding: '0',
    height: '16px',
    width: '16px',
  },
  countryAddInput: {
    maxWidth: '260px',
  },
  countryChipEditable: {
    display: 'inline-flex',
    alignItems: 'stretch',
    height: '28px',
    padding: '0 0 0 12px',
    borderRadius: '9999px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  countryChipLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingRight: '8px',
  },
  countryRemoveX: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '100%',
    border: 'none',
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    background: 'transparent',
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    padding: 0,
    fontSize: '14px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorPaletteRedForeground1,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
  },
  addCountryDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: '320px',
  },
  errorBar: {
    marginBottom: '12px',
    maxWidth: '100%',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  skeletonBlock: {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  skeletonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '14px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  skeletonNameCell: {
    flex: '0 0 160px',
  },
  skeletonCountriesCell: {
    flex: '1 1 auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    minWidth: 0,
  },
  skeletonPill: {
    height: '28px',
    borderRadius: '9999px',
  },
  skeletonActionsCell: {
    flex: '0 0 110px',
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  skeletonActionSquare: {
    height: '28px',
    width: '28px',
    borderRadius: '4px',
  },
  skeletonInputCell: {
    flex: '1 1 0',
    minWidth: '100px',
  },
  skeletonInput: {
    height: '32px',
    borderRadius: '4px',
  },
});
